import { Component, effect, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { VendorRolesService, VendorRoleWithStatuses, VendorStatusItem } from 'src/app/services/vendor-roles.service';
import { VendorRoleDialogComponent, VendorRoleDialogData } from '../vendor-role-dialog.component';
import { StatusTooltipComponent } from '../status-tooltip/status-tooltip.component';

@Component({
  standalone: true,
  selector: 'app-manage-roles',
  templateUrl: './manage-roles.component.html',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatTableModule,
    TranslateModule,
    StatusTooltipComponent,
  ],
})
export class ManageRolesComponent {
  orgId = input<number | null>(null);
  rolesChanged = output<void>();

  vendorRoles: VendorRoleWithStatuses[] = [];
  allStatuses: VendorStatusItem[] = [];
  loading = false;
  displayedColumns: string[] = ['roleName', 'description', 'active', 'statuses', 'actions'];

  constructor(
    private vendorRolesService: VendorRolesService,
    private dialog: MatDialog,
    private translate: TranslateService,
  ) {
    effect(() => {
      const id = this.orgId();
      this.loadRoles(id);
    });
  }

  private loadRoles(orgId: number | null): void {
    if (!orgId) {
      this.vendorRoles = [];
      this.allStatuses = [];
      return;
    }

    this.loading = true;
    this.vendorRolesService.getVendorRolesWithStatuses(orgId).subscribe({
      next: (res) => {
        this.vendorRoles = res.data;
        this.allStatuses = res.allStatuses;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  getVisibleStatusIds(role: VendorRoleWithStatuses): Set<number> {
    return new Set(role.statuses.map((s) => s.id));
  }

  addRole(): void {
    const orgId = this.orgId();
    if (!orgId) return;

    const dialogRef = this.dialog.open(VendorRoleDialogComponent, {
      width: '480px',
      data: {
        mode: 'create',
        orgId,
        allStatuses: this.allStatuses,
      } as VendorRoleDialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadRoles(this.orgId());
        this.rolesChanged.emit();
      }
    });
  }

  editRole(role: VendorRoleWithStatuses): void {
    const dialogRef = this.dialog.open(VendorRoleDialogComponent, {
      width: '480px',
      data: {
        mode: 'edit',
        orgId: role.org_id,
        role,
        allStatuses: this.allStatuses,
      } as VendorRoleDialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadRoles(this.orgId());
        this.rolesChanged.emit();
      }
    });
  }

  deleteRole(role: VendorRoleWithStatuses): void {
    const confirmed = confirm(this.translate.instant('Are you sure you want to delete this vendor role?'));
    if (!confirmed) return;

    this.vendorRolesService.deleteVendorRole(role.id, role.org_id).subscribe({
      next: () => {
        this.loadRoles(this.orgId());
        this.rolesChanged.emit();
      },
    });
  }
}
